import { datadogLogs } from '@datadog/browser-logs'
import { PfmContext } from 'containers/PageFlowManager/PageFlowManager'
import { AppState, AppStateContext } from 'containers/State/AppState'
import { PanelSupplierCode, ResponseChoiceValue } from 'model/common'
import {
  CategoricalResponseChoiceInput,
  ResponseOptionChoice,
  ResponseType,
  toCategoricalChoice
} from 'model/responseChoice'
import { useContext } from 'react'
import { coreRespondentUrl } from 'utils/coreRespondentUrl'
import { getNowTimeString, getUtcOffset } from 'utils/datetime'
import { usePostCallbackWithRetry } from './usePostCallback'
import { useQuestionnaireParams } from './useQuestionnaireParams'

interface AddCategoricalQuestionPayload {
  survey_id: string
  supplier_respondent_id: string
  panel_supplier_code: PanelSupplierCode
  categorical_choice: CategoricalResponseChoiceInput
  entry_position: number
  previous_entry_position: number | null
  context_position: number
  rendered_context_position: number
  question_type_code: string
  entering_datetime: string
  submission_datetime: string
  time_zone: string
  utc_offset: string
}

const useSendCategoricalQuestion = () => {
  const post = usePostCallbackWithRetry<AddCategoricalQuestionPayload>()
  const { surveyId, respondentId, panelSupplier } = useQuestionnaireParams()
  const {
    respondentProgress: [progress, setProgress]
  } = useContext<AppState>(AppStateContext)
  const { next } = useContext(PfmContext)

  const sendCategoricalQuestion = async ({
    questionId,
    entryPosition,
    renderedEntryPosition,
    contextPosition,
    renderedContextPosition,
    questionTypeCode,
    enteringDatetime,
    responseChoices
  }: {
    questionId: string
    entryPosition: number
    renderedEntryPosition: number
    contextPosition: number
    renderedContextPosition: number
    questionTypeCode: string
    enteringDatetime: string
    responseChoices: ResponseOptionChoice[]
  }) => {
    const payload: AddCategoricalQuestionPayload = {
      survey_id: surveyId,
      supplier_respondent_id: respondentId,
      panel_supplier_code: panelSupplier as PanelSupplierCode,
      categorical_choice: {
        matrix_title_id: null, // Questions don't have matrix context
        question_id: questionId,
        rendered_entry_position: renderedEntryPosition,
        row_position: null,
        rendered_row_position: null,
        response_type: ResponseType.Categorical,
        response_choices: responseChoices.map((choice) => ({
          response_option_id: choice.responseOptionId!,
          categorical_choice: toCategoricalChoice(
            choice.value as ResponseChoiceValue
          ),
          // choice.value is either "Selected"/"NotSelected" or actual user input text
          // Only populate text_entry when it contains user-entered text
          text_entry:
            typeof choice.value === 'string' &&
            choice.value !== ResponseChoiceValue.Selected &&
            choice.value !== ResponseChoiceValue.NotSelected
              ? choice.value
              : null,
          response_option_position: choice.position ?? 0,
          rendered_response_option_position:
            choice.renderedPosition ?? choice.position ?? 0
        }))
      },
      entry_position: entryPosition,
      previous_entry_position: progress?.previousEntryPosition ?? null,
      context_position: contextPosition,
      rendered_context_position: renderedContextPosition,
      question_type_code: questionTypeCode,
      entering_datetime: enteringDatetime,
      submission_datetime: getNowTimeString(),
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      utc_offset: getUtcOffset()
    }
    const url = coreRespondentUrl(
      `/surveys/${surveyId}/responses/categorical/question`
    )

    const requestStartTime = performance.now()

    const promise = post(payload, url)
      .then(() => {
        const requestDuration = performance.now() - requestStartTime
        datadogLogs.logger.info('Categorical question response was sent', {
          payload,
          url,
          requestDurationMs: Math.round(requestDuration)
        })
      })
      .catch((err) => {
        const requestDuration = performance.now() - requestStartTime
        if (progress) {
          setProgress({
            ...progress,
            isQualityTerminated: true
          })
          next()
        } else {
          throw new Error(
            'Could not find respondent progress in useSendCategoricalQuestion'
          )
        }
        datadogLogs.logger.error(
          'Network Failure: Categorical question response failed to be sent',
          {
            err,
            payload,
            url,
            payloadSize: JSON.stringify(payload).length,
            requestDurationMs: Math.round(requestDuration)
          }
        )
      })

    return promise
  }

  return { sendCategoricalQuestion }
}

export default useSendCategoricalQuestion
